import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, IconButton } from "@mui/material";
import { styled } from "@mui/styles";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { borderRadius, colors, fontSize, fontWeight, gaps, padding } from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import ColumnsConfig from "controls/global/columns-config/ColumnsConfig";
import type { MenuColumnItem } from "controls/global/columns-config/ColumnsConfig";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import { PremiumReceivablesReceiptRecord } from "entities/UIModel/PremiumReceivablesReceiptRecord";
import { useColumnSettings } from "utils/custom-hooks/useColumnSettings";
import {
  getAccountingReceivableListByCriteria,
  mapAccountingReceivableDtoToReceiptRecord,
} from "utils/api/accountingReceivableApi";
import AdvancedSearchPanel, { parseIsoDate } from "pages/utils/AdvancedSearchPanel";
import { serializeFilters } from "../usePremiumReceivableList";
import { useDebounced } from "utils/custom-hooks/useDebounced";
import { usePersistedRowsPerPage } from "utils/custom-hooks/usePersistedRowsPerPage";
import { useReceiptTypeOptions } from "utils/custom-hooks/useReceiptTypeOptions";
import ReceiptsTable from "./ReceiptsTable";
import { AR_GRID_NAME, buildArColumns, formatDisplayDate } from "./arColumns";
import { SelectSearchOption } from "../../policy-management/shared/SelectSearch";
import AddAccountReceivablesModal from "../modals/AddAccountReceivablesModal";
import { exportPremiumReceivable, PremiumReceivableCriteriaDto } from "utils/api/premiumReceivableApi";
import { orderBy as _orderBy } from "lodash";
import { useProfileSettingsCache } from "utils/context/ProfileSettingsContext";

// Maps UI record fields to the AccountingReceivableCriteria field names the
// stored proc binds to. `date` has no entry — only a ReceiptDateFrom/To range
// param exists (handled by the Advanced Search panel), no exact-match date
// param — so it stays client-side, see filteredRecords. @AgentName does an
// exact match (R.AgentName = @AgentName), same as @AgentID, not a LIKE.
const RECORD_TO_CRITERIA_FIELD: Partial<
  Record<keyof PremiumReceivablesReceiptRecord, string>
> = {
  AgentID: "AgentID",
  AgentName: "AgentName",
  AccountingPeriod: "AccountingPeriod",
  ReceiptDate: "ReceiptDate",
  ReceiptTypeCode: "ReceiptTypeCode",
  ReceiptNumber: "ReceiptNumber",
  ReceiptAmount: "ReceiptAmount",
  GLPRINumber: "GLPRINumber",
  GLSubNumber: "GLSubNumber",
  GLDeptNumber: "GLDeptNumber",
  BankAccountNumber: "BankAccountNumber",
  IsCancel: "IsCancel",
  Comments: "Comments",
};

function filtersToCriteria(filters: FilterMap): Record<string, unknown> {
  const criteria: Record<string, unknown> = {};
  Object.entries(filters).forEach(([field, value]) => {
    const criteriaField = RECORD_TO_CRITERIA_FIELD[field as keyof PremiumReceivablesReceiptRecord];
    if (!criteriaField || value == null) return;
    // IsCancel binds to a bool on the criteria entity, but its column dropdown
    // yields 1/0 — send it as a boolean rather than a number.
    criteria[criteriaField] =
      criteriaField === "IsCancel" ? Boolean(Number(value)) : value;
  });
  return serializeFilters(criteria);
}

// Maps UI record fields to the @SortColumn names the proc supports (raw name
// = ASC, "-"-prefixed = DESC). `comments` omitted — not supported, so that
// column is marked non-sortable in arColumns.tsx.
const RECORD_TO_SORT_COLUMN: Partial<
  Record<keyof PremiumReceivablesReceiptRecord, string>
> = {
  AgentID: "AgentID",
  AgentName: "AgentName",
  AccountingPeriod: "AccountingPeriod",
  ReceiptDate: "ReceiptDate",
  ReceiptTypeCode: "ReceiptTypeCode",
  ReceiptNumber: "ReceiptNumber",
  ReceiptAmount: "ReceiptAmount",
  GLPRINumber: "GLPRINumber",
  GLSubNumber: "GLSubNumber",
  GLDeptNumber: "GLDeptNumber",
  BankAccountNumber: "BankAccountNumber",
  IsCancel: "IsCancel",
};

function sortColumnToCriteria(sortColumn: string): Record<string, unknown> {
  if (!sortColumn) return {};
  const desc = sortColumn.startsWith("-");
  const field = (desc ? sortColumn.slice(1) : sortColumn) as keyof PremiumReceivablesReceiptRecord;
  const backendColumn = RECORD_TO_SORT_COLUMN[field];
  if (!backendColumn) return {};
  return { SortColumn: desc ? `-${backendColumn}` : backendColumn };
}

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const ChipRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  flexWrap: "wrap",
});

const Chip = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: gaps.xsmall1,
  padding: `${padding.xsmall1} ${padding.medium1}`,
  borderRadius: borderRadius.xlarge1,
  fontSize: fontSize.small,
  fontWeight: fontWeight.normal2,
  cursor: "pointer",
  userSelect: "none",
  border: `1px solid ${colors.grey09}`,
  backgroundColor: colors.white,
  color: colors.grey11,
  whiteSpace: "nowrap",
  "&.active": {
    backgroundColor: colors.blue08,
    color: colors.blue02,
    borderColor: colors.blue01,
  },
});

type RcptTypeChip = "all" | "JE" | "CHK" | "REC";

type HeaderFilters = Record<string, string | number | null>;
// `number` is needed for numeric column filters (e.g. IsCancel's 1/0 options).
type FilterMap = Record<string, string | string[] | number | boolean | null>;

type SearchCriteria = {
  ReceiptDateFrom?: string;
  ReceiptDateTo?: string;
  ReceiptAmountFrom?: number;
  ReceiptAmountTo?: number;
};

const CHIP_LABELS: Record<RcptTypeChip, string> = {
  all: "All",
  JE: "Journal Entries",
  CHK: "Checks",
  REC: "Receipts",
};

interface ReceiptsTabProps {
  userTimeZone?: string;
}

const ReceiptsTab = ({ userTimeZone }: ReceiptsTabProps) => {
// Matches Policy Inventory's Hide All, which keeps its first column visible.
const AR_PROTECTED_FIELDS: (keyof PremiumReceivablesReceiptRecord)[] = ["AgentID"];

  const [records, setRecords] = useState<PremiumReceivablesReceiptRecord[]>([]);
  // Column-header edits land here immediately (so the input feels responsive)
  // and are debounced below before being sent to the backend as search
  // criteria — no separate "Apply" step, the search is fully dynamic.
  const [pendingFilters, setPendingFilters] = useState<FilterMap>({});
  const appliedFilters = useDebounced(pendingFilters, 500);
  const [activeChip, setActiveChip] = useState<RcptTypeChip>("all");
  const [addOpen, setAddOpen] = useState(false);
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  const [advancedValues, setAdvancedValues] = useState<
    Record<string, string | OptionRenderType[]>
  >({});
  const [appliedHeaderFilters, setAppliedHeaderFilters] = useState<HeaderFilters>({});
  const [sortColumn, setSortColumn] = useState<string>("-ReceivedDate");
  const [{ profileSettings }] = useProfileSettingsCache();  
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>({});
  // Bumped on every chip click so the "Accounting Record Type" column's own
  // filter dropdown remounts and clears — otherwise a leftover selection
  // there (e.g. "JE") keeps narrowing the rows even after switching chips,
  // since the chip filter and the column filter both act on ReceiptTypeCode.
  const [rcptTypeFilterResetKey, setRcptTypeFilterResetKey] = useState(0);
  const [tableResetKey, setTableResetKey] = useState(0);
  const [pageSize, setPageSize] = usePersistedRowsPerPage(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  // Query-wide aggregates from the backend (over the whole filtered result
  // set, not just the current page) — computing these client-side from
  // `records` would only reflect the current page under server paging.
  const [totals, setTotals] = useState({ debits: 0, credits: 0, net: 0, distinctAgents: 0 });

  // Any change to search/sort criteria invalidates the current page — land
  // back on page 1 rather than requesting a page that may no longer exist
  // for the new result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [searchCriteria, appliedFilters, sortColumn]);

  useEffect(() => {
    let cancelled = false;
    // No AccountingPeriod scope — load across all periods rather than
    // silently hiding everything outside the current month.
    getAccountingReceivableListByCriteria({
      UserTimeZone: profileSettings?.userTimeZone || "",
      ListCount: pageSize,
      RequestedPage: currentPage,
      ...searchCriteria,
      ...filtersToCriteria(appliedFilters),
      ...sortColumnToCriteria(sortColumn),
    }).then((dtos) => {
      if (cancelled) return;
      setRecords(dtos.map(mapAccountingReceivableDtoToReceiptRecord));
      setTotalRows(dtos[0]?.TotalRows ?? 0);
      setTotalPages(dtos[0]?.TotalPages ?? 0);
      setTotals({
        debits: dtos[0]?.TotalDebits ?? 0,
        credits: dtos[0]?.TotalCredits ?? 0,
        net: dtos[0]?.TotalNet ?? 0,
        distinctAgents: dtos[0]?.TotalDistinctAgents ?? 0,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [profileSettings?.userTimeZone, searchCriteria, appliedFilters, sortColumn, pageSize, currentPage]);

  const handlePageSizeChange = useCallback(
    (next: number) => {
      setPageSize(next);
      setCurrentPage(1);
    },
    [setPageSize]
  );

  // The chips (All/Journal Entries/Checks/Receipts) and their counts only
  // reflect the current page, now that the grid is server-paginated —
  // they're a quick lens on what's loaded, not a full-result-set tally.
  const handleChipClick = useCallback((chip: RcptTypeChip) => {
    setActiveChip(chip);
    setPendingFilters((prev) => ({ ...prev, ReceiptTypeCode: null }));
    setRcptTypeFilterResetKey((k) => k + 1);
  }, []);

  const handleAdvancedChange = useCallback(
    (key: string, value: string | OptionRenderType[]) => {
      setAdvancedValues((prev) => {
        const next = { ...prev, [key]: value };
        return next;
      });
    },
    []
  );
  const formatValue = (key: string) => {
    const value = advancedValues[key];
    return typeof value === "string" ? value.trim() : "";
  };
  // Walks every field the panel holds rather than naming four of them, so a
  // field added to `advancedSections` reaches the criteria as soon as it has
  // a mapping. Empty fields are skipped — an absent key means "no filter".
  const handleAdvancedSearch = useCallback(() => {
    const amount = (key: string) => {
      const raw = formatValue(key);
      if (raw === "") return undefined;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : undefined;
    };

    const advSearchPayload: SearchCriteria = {};
    const dateFrom = formatValue("dateFrom");
    const dateTo = formatValue("dateTo");
    if (dateFrom) advSearchPayload.ReceiptDateFrom = dateFrom;
    if (dateTo) advSearchPayload.ReceiptDateTo = dateTo;
    const min = amount("minimumAmount");
    const max = amount("maximumAmount");
    if (min !== undefined) advSearchPayload.ReceiptAmountFrom = min;
    if (max !== undefined) advSearchPayload.ReceiptAmountTo = max;
    setSearchCriteria(advSearchPayload);
  }, [advancedValues]);

  // Clear empties the panel and drops back to the unfiltered load. Returning
  // the previous object when there was nothing committed keeps `searchCriteria`
  // referentially stable, so Clear on an untouched panel doesn't refetch.
  const handleAdvancedClear = useCallback(() => {
    setAdvancedValues({});
    setSearchCriteria((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, []);

  const chipFilteredRecords = useMemo(
    () => (activeChip === "all" ? records : records.filter((r) => r.ReceiptTypeCode === activeChip)),
    [records, activeChip]
  );

  const chipCounts = useMemo(
    () => ({
      all: records.length,
      JE: records.filter((r) => r.ReceiptTypeCode === "JE").length,
      CHK: records.filter((r) => r.ReceiptTypeCode === "CHK").length,
      REC: records.filter((r) => r.ReceiptTypeCode     === "REC").length,
    }),
    [records]
  );

  const CLIENT_ONLY_FIELDS = ["date"] as const;

  const filteredRecords = useMemo(() => {
    let result = chipFilteredRecords;

    CLIENT_ONLY_FIELDS.forEach((key) => {
      const value = appliedFilters[key];
      if (!value || typeof value !== "string") return;
      const searchStr = value.toLowerCase().trim();
      if (!searchStr) return;
      result = result.filter((r) => {
        const rawValue = String(
          r[key as keyof PremiumReceivablesReceiptRecord] ?? ""
        );
        // `date` is stored as yyyy-MM-dd for correct sorting — match against
        // what's actually displayed (MM/dd/yyyy), not the raw stored value.
        const fieldValue = (key === "date" ? formatDisplayDate(rawValue) : rawValue).toLowerCase();
        return fieldValue.includes(searchStr);
      });
    });

    return result;
  }, [chipFilteredRecords, appliedFilters]);


  const handleFiltersChange = useCallback(
    (name: keyof PremiumReceivablesReceiptRecord, value: string | number | boolean | null) => {
      setPendingFilters((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setPendingFilters({});
    setActiveChip("all");
    setAdvancedValues({});
    setSearchCriteria((prev) => (Object.keys(prev).length === 0 ? prev : {}));
    setSortColumn("");
    setAppliedHeaderFilters({});
    setRcptTypeFilterResetKey((k) => k + 1);
    setTableResetKey((k) => k + 1);
  }, []);

  const rcptTypeOptions = useReceiptTypeOptions();

  const cancelFlagOptions: SelectSearchOption<boolean>[] = useMemo(
    () => [
      { label: "Yes", value: true },
      { label: "No", value: false },
    ],
    []
  );

  const defaultColumns = useMemo(
    () =>
      buildArColumns({
        rcptTypeOptions,
        cancelFlagOptions,
        onFiltersChange: handleFiltersChange,
        rcptTypeFilterResetKey,
      }),
    [rcptTypeOptions, cancelFlagOptions, handleFiltersChange, rcptTypeFilterResetKey]
  );
  
  // What the export must send so the file matches the grid: the same criteria
  // the list fetch builds. `advancedValues` is deliberately not spread in —
  // it's keyed by panel field name, and `searchCriteria` already holds the
  // mapped version of everything the panel committed.
  const appliedCriteria = useMemo(() => {
    const criteria: Record<string, unknown> = {
      ...appliedHeaderFilters,
      ...searchCriteria,
      ...filtersToCriteria(appliedFilters),
      ...advancedValues
    };

    // The chip and the "Accounting Record Type" column filter both act on
    // ReceiptTypeCode; the column filter is the more specific of the two.
    if (activeChip !== "all" && criteria.ReceiptTypeCode == null) {
      criteria.ReceiptTypeCode = activeChip;
    }

    return criteria;
  }, [appliedHeaderFilters, searchCriteria, appliedFilters, activeChip]);
  const {
    columns,
    hiddenColumns,
    onColumnResize,
    onColumnsModified,
    setColumnHidden,
    showAllColumns,
    hideAllColumns,
  } = useColumnSettings<PremiumReceivablesReceiptRecord>(
    AR_GRID_NAME,
    defaultColumns,
    AR_PROTECTED_FIELDS
  );

  const columnOptions: MenuColumnItem<PremiumReceivablesReceiptRecord>[] = useMemo(
    () =>
      defaultColumns
        .filter((c) => c.field !== "uniqueIdentifier")
        .map((c) => ({
          field: c.field,
          name: c.name,
          checked: !hiddenColumns.includes(c.field),
        })),
    [defaultColumns, hiddenColumns]
  );

  const handleColumnChecked = useCallback(
    (field: keyof PremiumReceivablesReceiptRecord, wasChecked: boolean) => {
      setColumnHidden(field, wasChecked);
    },
    [setColumnHidden]
  );

  const exportedColumns = useMemo(
    () =>
      _orderBy(
        columns.filter((c) => !hiddenColumns.includes(c.field)),
        "position"
      ).map((c) => String(c.field)),
    [columns, hiddenColumns]
  );
  const handleExport = useCallback(async () => {
    const searchCriteria: Partial<PremiumReceivableCriteriaDto> = {
      ...serializeFilters(appliedCriteria ?? {}),
      UserTimeZone: userTimeZone,
      SortColumn: sortColumn,
      ListCount: 0,
      RequestedPage: 0,
    };
    await exportPremiumReceivable({
      ExportColumns: exportedColumns,
      SearchCriteria: searchCriteria,
    },
      "AccountingReceivable/Export",
      "Account Receivables"
    );
  }, [appliedCriteria, exportedColumns, sortColumn, userTimeZone]);

  const dateTypeOptions: OptionRenderType[] = [
    { label: "Receipt Date", value: "receiptDate" },
  ];

  const advancedDate = (key: string) => parseIsoDate(advancedValues[key]);
  const minimumAmount = formatValue("minimumAmount");
  const minimumAmountValue = minimumAmount === "" ? null : Number(minimumAmount);

  const advancedSections = [
    {
      title: "Date",
      fields: [
        {
          key: "dateLabel",
          label: "Date :",
          type: "label" as const,
          inline: true,
        },
        {
          key: "dateType",
          label: "Date Type",
          type: "select" as const,
          options: dateTypeOptions,
          inline: true,
        },
        {
          key: "dateFrom",
          label: "",
          type: "date" as const,
          maxDate: advancedDate("dateTo"),
        },
        { key: "dateRangeSeparator", label: "—", type: "separator" as const },
        {
          key: "dateTo",
          label: "",
          type: "date" as const,
          minDate: advancedDate("dateFrom"),
        },
      ],
    },
    {
      title: "Amount",
      fields: [
        {
          key: "amountLabel",
          label: "Amount :",
          type: "label" as const,
          inline: true,
        },
        { key: "minimumAmount", label: "Minimum ($)", type: "number" as const },
        {
          key: "maximumAmount",
          label: "Maximum ($)",
          type: "number" as const,
          minValue: minimumAmountValue,
        },
      ],
    },
  ];

  return (
    <TabContainer>
      <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1, justifyContent: "space-between", flexWrap: "wrap" }}>
        <ChipRow>
          {(["all", "JE", "CHK", "REC"] as RcptTypeChip[]).map((chip) => (
            <Chip
              key={chip}
              className={activeChip === chip ? "active" : ""}
              onClick={() => handleChipClick(chip)}
            >
              {CHIP_LABELS[chip]} ({chipCounts[chip]})
            </Chip>
          ))}
        </ChipRow>
        <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1 }}>
          <ActionButton
            variant="outlined"
            color="primary"
            onClick={() => setAddOpen(true)}
          >
            Add Accounting Receivable Entry
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </ActionButton>
          <IconButton
            color="primary"
            onClick={() => setAdvancedSearchOpen((o) => !o)}
            sx={{ backgroundColor: advancedSearchOpen ? colors.blue08 : "transparent" }}
          >
            <SearchIcon />
          </IconButton>
          <ColumnsConfig
            options={columnOptions}
            onChecked={handleColumnChecked}
            onShowAll={showAllColumns}
            onHideAll={hideAllColumns}
            iconOnly
          />
          <IconButton color="primary" onClick={handleExport}>
            <FileDownloadOutlinedIcon />
          </IconButton>
        </Box>
      </Box>

      <AdvancedSearchPanel
        open={advancedSearchOpen}
        sections={advancedSections}
        values={advancedValues}
        onChange={handleAdvancedChange}
        onClear={handleAdvancedClear}
        onSearch={handleAdvancedSearch}
      />

      <ReceiptsTable
        key={tableResetKey}
        records={filteredRecords}
        columns={columns}
        hiddenColumns={hiddenColumns}
        totalRows={totalRows}
        totalPages={totalPages}
        rowsPerPage={pageSize}
        onRowsPerPageChange={handlePageSizeChange}
        page={currentPage}
        onPageChange={setCurrentPage}
        totals={totals}
        onColumnResize={onColumnResize}
        onColumnsModified={onColumnsModified}
        sortColumn={sortColumn}
        onSortChange={setSortColumn}
      />

      <AddAccountReceivablesModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
      />
    </TabContainer>
  );
};

export default ReceiptsTab;
